import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';
import { getClubs, portalErrorMessage } from '../../features/portal/portalService';
import {
  secretaryClubPath,
  secretaryRinkQrPrintPath,
  secretarySectionQrPrintPath
} from './portalPaths';
import {
  PortalCounts,
  PortalEmpty,
  PortalError,
  PortalLoading,
  PortalPageHeader,
  PortalPrintActions
} from './portalCommon';

const PortalClubs = () => {
  const { eventId } = useParams();
  const { token } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  const load = useCallback(() => {
    if (!token || !eventId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    getClubs(token)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((requestError) => {
        if (!cancelled) setError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, eventId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load, retry]);

  const clubs = data?.clubs || [];

  return (
    <Box>
      <PortalPageHeader
        title="Clubs"
        subtitle="Choose a club to view its sections and games"
        action={
          <PortalPrintActions
            rinkTo={secretaryRinkQrPrintPath(eventId)}
            sectionTo={secretarySectionQrPrintPath(eventId)}
          />
        }
      />

      {loading && <PortalLoading />}
      {!loading && error && <PortalError message={error} onRetry={() => setRetry((n) => n + 1)} />}

      {!loading && !error && clubs.length === 0 && (
        <PortalEmpty>
          No clubs are set up for this event yet. Games with no venue can still be found once
          they are added.
        </PortalEmpty>
      )}

      {!loading && !error && clubs.length > 0 && (
        <Paper elevation={2}>
          <List disablePadding>
            {clubs.map((club, index) => (
              <Box key={club.venueId}>
                {index > 0 && <Divider />}
                <ListItemButton
                  component={RouterLink}
                  to={secretaryClubPath(eventId, club.venueId)}
                  sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <span>{club.venueName}</span>
                        {club.venueId === '__unassigned__' && (
                          <Chip size="small" color="warning" label="No venue" />
                        )}
                      </Box>
                    }
                    secondary={<PortalCounts {...club} />}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <ChevronRightIcon color="action" />
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default PortalClubs;
